/**
 * 회원 배터리 상세 페이지
 *
 * MemberBatteryList에서 처음엔 행을 클릭하면 그 자리에서 펼치는(inline expand) 방식으로
 * 만들었었는데, 기기 수가 수천 대 단위로 늘어나면 목록 페이지가 모든 기기의 전체 날짜별
 * 기록을 다 들고 있어야 해서 무거워진다. 그래서 MemberDetail과 동일하게 목록→상세 페이지
 * 전환 방식으로 바꾸고, 상세 페이지는 이 기기 하나의 기록만
 * GET /api/admin/device-daily-stats/device/{address} 로 그때그때 조회한다.
 *
 * 기기 주소(MAC)는 콜론(:)을 포함하므로 URL 파라미터는 encodeURIComponent로 넘어온다.
 *
 * 정렬: MemberList의 정렬 UX(헤더 클릭 → asc/desc 토글, 아이콘 표시)를 그대로 따른다.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { dailyStatsService } from "../../services/dailyStatsService";
import { userService } from "../../services/userService";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import StatsCard from "../../components/ui/StatsCard";
import BackButton from "../../components/ui/BackButton";
import EmptyDetailState from "../../components/ui/EmptyDetailState";
import ConfirmModal from "../../components/ui/ConfirmModal";
import { formatDate, formatDateTime } from "../../utils/format";

type SortField = "statDate" | "clickCount" | "lastBatteryV" | "updatedAt";
type SortDirection = "asc" | "desc";
const ALL_PHONE_NUMBERS = "all";
const MISSING_PHONE_NUMBER = "missing";

const COLUMNS: { label: string; field: SortField }[] = [
  { label: "날짜",          field: "statDate" },
  { label: "클릭수",        field: "clickCount" },
  { label: "배터리",        field: "lastBatteryV" },
  { label: "서버 수신 시각", field: "updatedAt" },
];

export default function MemberBatteryDetail() {
  const { address } = useParams<{ address: string }>();
  const deviceAddress = address ? decodeURIComponent(address) : "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const sortField     = (searchParams.get("sort") ?? "statDate") as SortField;
  const sortDirection = (searchParams.get("dir")  ?? "desc") as SortDirection;
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [phoneFilter, setPhoneFilter] = useState(ALL_PHONE_NUMBERS);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleSort = useCallback(
    (field: SortField) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (sortField === field) {
          next.set("dir", sortDirection === "asc" ? "desc" : "asc");
        } else {
          next.set("sort", field);
          next.set("dir", "asc");
        }
        return next;
      }, { replace: true });
    },
    [sortField, sortDirection, setSearchParams]
  );

  const { data: history, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["dailyStats", "device", deviceAddress],
    queryFn: () => dailyStatsService.getByDevice(deviceAddress),
    enabled: !!deviceAddress,
  });

  // 버전 정보는 일별 통계에 없어서 회원 목록에서 기기 주소로 찾는다 (users는 회원 수 규모라 가벼움)
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ["users"],
    queryFn: userService.getAllUsers,
  });

  const deleteMutation = useMutation({
    mutationFn: () => dailyStatsService.deleteByDevice(deviceAddress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dailyStats"] });
      setShowDeleteModal(false);
      navigate("/admin/member/battery");
    },
  });

  const dateFilteredHistory = useMemo(() => {
    if (!history) return [];
    return history.filter((item) =>
      (!fromDate || item.statDate >= fromDate) &&
      (!toDate || item.statDate <= toDate) &&
      (phoneFilter === ALL_PHONE_NUMBERS ||
        (phoneFilter === MISSING_PHONE_NUMBER
          ? !item.phoneNumber
          : item.phoneNumber === phoneFilter))
    );
  }, [history, fromDate, toDate, phoneFilter]);

  const phoneNumbers = useMemo(
    () => Array.from(new Set(history?.map((item) => item.phoneNumber).filter((phone): phone is string => !!phone))).sort(),
    [history]
  );
  const hasMissingPhoneNumber = history?.some((item) => !item.phoneNumber) ?? false;

  const sorted = useMemo(() => {
    const list = [...dateFilteredHistory];
    list.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;
      switch (sortField) {
        case "statDate":     aValue = a.statDate; bValue = b.statDate; break;
        case "clickCount":   aValue = a.clickCount; bValue = b.clickCount; break;
        case "lastBatteryV": aValue = a.lastBatteryV; bValue = b.lastBatteryV; break;
        case "updatedAt":    aValue = a.updatedAt ?? ""; bValue = b.updatedAt ?? ""; break;
        default:             return 0;
      }
      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [dateFilteredHistory, sortField, sortDirection]);

  if (statsLoading || usersLoading) return <LoadingSpinner />;
  if (statsError) return <ErrorAlert />;

  if (!history || history.length === 0) {
    return (
      <EmptyDetailState
        icon="fas fa-battery-empty"
        message="이 기기의 배터리 기록을 찾을 수 없습니다"
        backTo="/admin/member/battery"
        backLabel="배터리 관리로 돌아가기"
      />
    );
  }

  // "가장 마지막" 기준은 항상 날짜 최신순 — 정렬 상태(sorted)와 무관하게 별도 계산
  const latest = [...history].sort((a, b) => b.statDate.localeCompare(a.statDate))[0];
  const totalClicks = history.reduce((sum, h) => sum + h.clickCount, 0);
  const reporterCount = new Set(history.map((h) => h.phoneNumber ?? h.sourceUserId ?? "LEGACY_UNKNOWN")).size;
  const deviceVersion = users?.find((u) => u.deviceAddress === deviceAddress)?.deviceVersion ?? "-";

  // 최초 연동일(가장 오래된 기록 날짜) → 오늘까지 경과 일수.
  // statDate는 "yyyy-MM-dd" 문자열이라 Date로 파싱해도 둘 다 UTC 자정 기준이라 시간대 오차 없이 일수 차이만 나옴.
  const firstRecordDate = history.reduce(
    (min, h) => (h.statDate < min ? h.statDate : min),
    history[0].statDate
  );
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const daysSinceFirst =
    Math.floor((new Date(todayStr).getTime() - new Date(firstRecordDate).getTime()) / 86_400_000) + 1;

  const sortIcon = (field: SortField) =>
    sortField === field
      ? sortDirection === "asc" ? "fa-arrow-up text-primary" : "fa-arrow-down text-primary"
      : "fa-sort text-gray-400";

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">회원 배터리 상세</h1>
          <p className="text-gray-500 mt-2">기기 {deviceAddress}의 전체 기록</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="rounded-lg border border-text-red/30 bg-red-50 px-4 py-2 text-sm font-medium text-text-red transition-colors hover:bg-red-100"
          >
            <i className="fas fa-trash-alt mr-2"></i>수신 데이터 삭제
          </button>
          <BackButton to="/admin/member/battery" label="배터리 관리로" />
        </div>
      </div>

      {/* Device Summary Card */}
      <div className="bg-primary rounded-xl shadow-lg p-8 mb-6 text-white">
        <div className="flex items-center space-x-6">
          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center text-3xl backdrop-blur-sm">
            <i className="fas fa-mobile-alt"></i>
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2 font-mono">{deviceAddress}</h2>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-white/80">
              <span>
                <i className="fas fa-phone mr-2"></i>
                {latest.phoneNumber ?? "미수집"}
              </span>
              <span>
                <i className="fas fa-mobile-alt mr-2"></i>
                수신 전화번호 {reporterCount}개
              </span>
              <span>
                <i className="fas fa-mobile-alt mr-2"></i>
                {deviceVersion}
              </span>
              <span>
                <i className="fas fa-plug mr-2"></i>
                최초 연동 {formatDate(firstRecordDate)}
              </span>
              <span>
                <i className="fas fa-calendar-alt mr-2"></i>
                최근 기록 {formatDate(latest.statDate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatsCard
          label="마지막 배터리"
          value={`${latest.lastBatteryV.toFixed(2)}V`}
          icon="fas fa-battery-three-quarters"
          borderColor="border-marker-orange"
          iconColor="text-marker-orange"
          bgColor="bg-marker-orange/10"
          valueColor="text-marker-orange"
        />
        <StatsCard
          label="총 클릭수"
          value={`${totalClicks.toLocaleString()}회`}
          icon="fas fa-hand-pointer"
          borderColor="border-primary"
          iconColor="text-primary"
          bgColor="bg-primary/10"
        />
        <StatsCard
          label="사용 기간"
          value={`${daysSinceFirst.toLocaleString()}일째`}
          icon="fas fa-plug"
          borderColor="border-blue-500"
          iconColor="text-blue-600"
          bgColor="bg-blue-500/10"
          valueColor="text-blue-600"
        />
      </div>

      {/* History Table */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-150 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <h3 className="text-lg font-semibold text-gray-800">
            <i className="fas fa-history mr-2 text-primary"></i>
            일별 전체 기록
          </h3>
          <div className="flex flex-wrap items-center gap-2 text-sm lg:flex-nowrap">
            <label className="w-7 shrink-0 text-gray-500" htmlFor="battery-from-date">날짜</label>
            <input
              id="battery-from-date"
              type="date"
              value={fromDate}
              max={toDate || undefined}
              onChange={(event) => setFromDate(event.target.value)}
              className="h-10 w-36 shrink-0 rounded-lg border border-gray-200 bg-white px-3 text-gray-700 shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <span className="w-3 shrink-0 text-center text-gray-400">~</span>
            <input
              type="date"
              value={toDate}
              min={fromDate || undefined}
              onChange={(event) => setToDate(event.target.value)}
              className="h-10 w-36 shrink-0 rounded-lg border border-gray-200 bg-white px-3 text-gray-700 shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              aria-label="종료 날짜"
            />
            <div className="relative w-44 shrink-0">
              <i className="fas fa-phone pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-primary"></i>
              <select
                value={phoneFilter}
                onChange={(event) => setPhoneFilter(event.target.value)}
                className="h-10 w-full appearance-none rounded-lg border border-primary/50 bg-primary-lighter py-2 pl-8 pr-9 font-medium text-gray-700 shadow-sm transition-colors hover:border-primary focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                aria-label="수집 전화번호 필터"
              >
                <option value={ALL_PHONE_NUMBERS}>전체 전화번호</option>
                {phoneNumbers.map((phone) => <option key={phone} value={phone}>{phone}</option>)}
                {hasMissingPhoneNumber && <option value={MISSING_PHONE_NUMBER}>전화번호 미수집</option>}
              </select>
              <i className="fas fa-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500"></i>
            </div>
            <button
              type="button"
              onClick={() => { setFromDate(""); setToDate(""); setPhoneFilter(ALL_PHONE_NUMBERS); }}
              disabled={!fromDate && !toDate && phoneFilter === ALL_PHONE_NUMBERS}
              className="h-10 w-14 shrink-0 rounded-lg text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:pointer-events-none disabled:opacity-0"
            >
              초기화
            </button>
            <span className="w-12 shrink-0 text-right text-gray-500">총 {sorted.length}건</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-150">
            <thead className="bg-gradient-to-r from-primary-lighter to-bg-light">
              <tr>
                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  전화번호
                </th>
                {COLUMNS.map(({ label, field }) => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-primary-lighter/50 transition-colors"
                  >
                    <div className="flex items-center justify-center space-x-1">
                      <span>{label}</span>
                      <i className={`fas ${sortIcon(field)}`}></i>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-150">
              {sorted.map((h) => (
                <tr key={h.id} className="hover:bg-primary-lighter transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {h.phoneNumber ?? "전화번호 미수집"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                    <i className="fas fa-calendar-alt mr-2 text-gray-400"></i>{formatDate(h.statDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-primary/10 text-primary border border-primary/30">
                      <i className="fas fa-hand-pointer mr-1"></i>{h.clickCount.toLocaleString()}회
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="px-3 py-1 inline-flex items-center text-xs leading-5 font-semibold rounded-full bg-marker-orange/10 text-marker-orange border border-marker-orange/30">
                      <i className="fas fa-battery-three-quarters mr-1"></i>{h.lastBatteryV.toFixed(2)}V
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                    {h.updatedAt ? formatDateTime(h.updatedAt) : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showDeleteModal && (
        <ConfirmModal
          title="이 기기의 수신 데이터를 모두 삭제하시겠습니까?"
          description={`기기 ${deviceAddress}의 배터리·클릭 수신 기록 ${history.length}건을 모든 전화번호 기준으로 삭제합니다. 기기 등록 및 회원정보는 삭제되지 않으며, 삭제된 기록은 복구할 수 없습니다.`}
          confirmLabel="전체 삭제"
          isLoading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
